import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon,
  KeyIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  ShieldCheckIcon,
  TruckIcon,
  UserIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageLayout from '../../components/PageLayout';
import { platformAPI } from '../../services/api';

type PlatformRole = 'admin' | 'user' | 'driver';
type KycStatus = 'verified' | 'pending' | 'rejected' | 'none';

type PlatformUser = {
  id: string;
  email: string;
  role: string;
  kyc_status: string;
  company_id: string;
  company_name: string | null;
  company_slug: string | null;
  created_at: string;
  updated_at: string;
};

type CompanyOption = {
  id: string;
  name: string;
  slug: string;
};

const INITIAL_CREATE_FORM = {
  email: '',
  password: '',
  confirmPassword: '',
};

const INITIAL_EDIT_FORM = {
  email: '',
  role: 'admin' as PlatformRole,
  kyc_status: 'verified' as KycStatus,
};

function getBaseHost(): string {
  if (typeof window === 'undefined') return 'modalos.com';

  const segments = window.location.hostname.split('.');
  return segments.length > 1 ? segments.slice(1).join('.') : window.location.hostname;
}

function getCompanyUrl(slug?: string | null): string | null {
  if (!slug) return null;
  if (typeof window === 'undefined') return `https://${slug}.modalos.com`;

  const port = window.location.port ? `:${window.location.port}` : '';
  return `${window.location.protocol}//${slug}.${getBaseHost()}${port}`;
}

function getInitials(email: string): string {
  return email.slice(0, 2).toUpperCase();
}

function roleLabel(role: string): string {
  if (role === 'user') return 'Customer';
  if (role === 'admin') return 'Admin';
  if (role === 'driver') return 'Driver';
  return role;
}

function roleBadgeClass(role: string): string {
  if (role === 'admin') return 'border border-outline/70 bg-primary/15 text-on-surface';
  if (role === 'user') return 'border border-outline/70 bg-info-container text-on-info-container';
  if (role === 'driver') return 'border border-outline/70 bg-success-container text-on-success-container';
  return 'border border-outline/70 bg-surface-container-low text-on-surface';
}

function kycBadgeClass(status: string): string {
  if (status === 'verified') return 'border border-outline/70 bg-success-container text-on-success-container';
  if (status === 'pending') return 'border border-outline/70 bg-warning-container text-on-warning-container';
  if (status === 'rejected') return 'border border-outline/70 bg-error-container text-on-error-container';
  return 'border border-outline/70 bg-surface-container-low text-on-surface';
}

function formatDate(value?: string): string {
  if (!value) return 'N/A';
  return new Date(value).toLocaleDateString();
}

function PlatformUsers() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState<'all' | PlatformRole>('all');
  const [kycFilter, setKycFilter] = useState<'all' | KycStatus>('all');
  const [editingUser, setEditingUser] = useState<PlatformUser | null>(null);
  const [editForm, setEditForm] = useState(INITIAL_EDIT_FORM);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(INITIAL_CREATE_FORM);
  const [tempPassword, setTempPassword] = useState<{ userId: string; password: string } | null>(null);

  const { data: users = [], isLoading } = useQuery('platform-users-directory', () => platformAPI.getUsers(), {
    select: (response) => response.data,
  });

  const { data: companies = [] } = useQuery('platform-companies-directory', () => platformAPI.getCompanies(), {
    select: (response) =>
      response.data.map((company: any) => ({
        id: company.id,
        name: company.name,
        slug: company.slug,
      })),
  });

  const updateMutation = useMutation(
    (data: { id: string; updates: typeof INITIAL_EDIT_FORM }) => platformAPI.updateUser(data.id, data.updates),
    {
      onSuccess: () => {
        toast.success('User updated');
        queryClient.invalidateQueries('platform-users-directory');
        setEditingUser(null);
        setEditForm(INITIAL_EDIT_FORM);
      },
      onError: () => {
        toast.error('Failed to update user');
      },
    },
  );

  const resetPasswordMutation = useMutation((id: string) => platformAPI.resetUserPassword(id), {
    onSuccess: (response, id) => {
      setTempPassword({ userId: id, password: response.data.temporaryPassword });
      toast.success('Password reset successfully');
    },
    onError: () => {
      toast.error('Failed to reset password');
    },
  });

  const createMutation = useMutation(
    (data: { email: string; password: string }) => platformAPI.createUser(data),
    {
      onSuccess: () => {
        toast.success('Platform admin created');
        queryClient.invalidateQueries('platform-users-directory');
        setShowCreate(false);
        setCreateForm(INITIAL_CREATE_FORM);
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to create user');
      },
    },
  );

  const normalizedSearch = search.trim().toLowerCase();
  const filteredUsers = users.filter((user: PlatformUser) => {
    const matchesSearch =
      !normalizedSearch ||
      [user.email, user.company_name || '', user.company_slug || '']
        .some((value) => value.toLowerCase().includes(normalizedSearch));
    const matchesCompany = companyFilter === 'all' || user.company_id === companyFilter;
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesKyc = kycFilter === 'all' || user.kyc_status === kycFilter;

    return matchesSearch && matchesCompany && matchesRole && matchesKyc;
  });

  const totalUsers = users.length;
  const admins = users.filter((user: PlatformUser) => user.role === 'admin').length;
  const customers = users.filter((user: PlatformUser) => user.role === 'user').length;
  const drivers = users.filter((user: PlatformUser) => user.role === 'driver').length;
  const verifiedUsers = users.filter((user: PlatformUser) => user.kyc_status === 'verified').length;
  const representedTenantCount = new Set(
    users
      .map((user: PlatformUser) => user.company_id)
      .filter(Boolean),
  ).size;

  const handleCreate = () => {
    if (!createForm.email || !createForm.password) {
      toast.error('Email and password are required');
      return;
    }
    if (createForm.password !== createForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (createForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    createMutation.mutate({ email: createForm.email, password: createForm.password });
  };

  const startEdit = (user: PlatformUser) => {
    setEditingUser(user);
    setEditForm({
      email: user.email,
      role: (user.role as PlatformRole) || 'admin',
      kyc_status: (user.kyc_status as KycStatus) || 'none',
    });
  };

  const saveEdit = () => {
    if (!editingUser) return;
    updateMutation.mutate({ id: editingUser.id, updates: editForm });
  };

  const handleResetPassword = (user: PlatformUser) => {
    if (window.confirm(`Reset password for ${user.email}? This will generate a temporary password.`)) {
      resetPasswordMutation.mutate(user.id);
    }
  };

  return (
    <PageLayout className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-on-surface">Platform Users</h1>
          <p className="mt-2 max-w-3xl text-on-surface-variant">
            Manage identity, company placement, and credential recovery across the platform from one operational view.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="btn-primary inline-flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Create Platform Admin</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="card p-4 flex items-center gap-3">
          <UsersIcon className="h-8 w-8 text-blue-600" />
          <div>
            <p className="text-sm text-on-surface-variant">Users</p>
            <p className="text-xl font-bold text-on-surface">{totalUsers}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <ShieldCheckIcon className="h-8 w-8 text-violet-600" />
          <div>
            <p className="text-sm text-on-surface-variant">Admins</p>
            <p className="text-xl font-bold text-on-surface">{admins}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <UserIcon className="h-8 w-8 text-blue-600" />
          <div>
            <p className="text-sm text-on-surface-variant">Customers</p>
            <p className="text-xl font-bold text-on-surface">{customers}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <TruckIcon className="h-8 w-8 text-emerald-600" />
          <div>
            <p className="text-sm text-on-surface-variant">Drivers</p>
            <p className="text-xl font-bold text-on-surface">{drivers}</p>
          </div>
        </div>
      </div>

      {tempPassword && (
        <div className="card p-4 border border-outline/70 bg-info-container">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-on-info-container">Temporary Password Generated</p>
              <p className="mt-1 text-sm text-on-info-container">
                Copy this value now. It is only returned once for user {tempPassword.userId}.
              </p>
              <code className="mt-3 block text-sm font-mono bg-surface-container-lowest px-3 py-2 rounded border border-outline/70 select-all break-all text-on-surface">
                {tempPassword.password}
              </code>
            </div>
            <button onClick={() => setTempPassword(null)} className="text-on-info-container/70 hover:text-on-info-container">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      <div className="card p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-2.5 text-on-surface-variant" />
            <input
              type="text"
              placeholder="Search by email or tenant..."
              className="input-field pl-10"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <select
            className="input-field"
            value={companyFilter}
            onChange={(event) => setCompanyFilter(event.target.value)}
          >
            <option value="all">All companies</option>
            {companies.map((company: CompanyOption) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
          <select
            className="input-field"
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value as 'all' | PlatformRole)}
          >
            <option value="all">All roles</option>
            <option value="admin">Admin</option>
            <option value="user">Customer</option>
            <option value="driver">Driver</option>
          </select>
          <select
            className="input-field"
            value={kycFilter}
            onChange={(event) => setKycFilter(event.target.value as 'all' | KycStatus)}
          >
            <option value="all">All KYC states</option>
            <option value="verified">Verified</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
            <option value="none">None</option>
          </select>
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          <span className="inline-flex px-2.5 py-1 rounded-full border border-outline/70 bg-info-container text-on-info-container font-medium">
            {filteredUsers.length} visible
          </span>
          <span className="inline-flex px-2.5 py-1 rounded-full border border-outline/70 bg-success-container text-on-success-container font-medium">
            {verifiedUsers} verified KYC
          </span>
          <span className="inline-flex px-2.5 py-1 rounded-full border border-outline/70 bg-primary/15 text-on-surface font-medium">
            {representedTenantCount} tenants represented
          </span>
          <span className="inline-flex px-2.5 py-1 rounded-full border border-outline/70 bg-surface-container-low text-on-surface font-medium">
            {companies.length} total companies
          </span>
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-container-lowest rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-on-surface">Create Platform Admin</h3>
                <p className="mt-1 text-sm text-on-surface-variant">This account will be provisioned with platform-level admin access.</p>
              </div>
              <button onClick={() => setShowCreate(false)} className="text-on-surface-variant hover:text-on-surface">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Email</label>
                <input
                  type="email"
                  className="input-field"
                  placeholder="admin@example.com"
                  value={createForm.email}
                  onChange={(event) => setCreateForm({ ...createForm, email: event.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Password</label>
                <input
                  type="password"
                  className="input-field"
                  placeholder="Minimum 8 characters"
                  value={createForm.password}
                  onChange={(event) => setCreateForm({ ...createForm, password: event.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Confirm Password</label>
                <input
                  type="password"
                  className="input-field"
                  placeholder="Re-enter password"
                  value={createForm.confirmPassword}
                  onChange={(event) => setCreateForm({ ...createForm, confirmPassword: event.target.value })}
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreate(false);
                  setCreateForm(INITIAL_CREATE_FORM);
                }}
                className="px-4 py-2 text-sm font-medium text-on-surface-variant bg-surface-container-low hover:bg-surface-container-highest hover:text-on-surface rounded-md"
              >
                Cancel
              </button>
              <button onClick={handleCreate} disabled={createMutation.isLoading} className="btn-primary">
                {createMutation.isLoading ? 'Creating...' : 'Create Admin'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-container-lowest rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-on-surface">Edit User</h3>
                <p className="mt-1 text-sm text-on-surface-variant">
                  Update identity and access for {editingUser.email}.
                </p>
              </div>
              <button onClick={() => setEditingUser(null)} className="text-on-surface-variant hover:text-on-surface">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Email</label>
                <input
                  type="email"
                  className="input-field"
                  value={editForm.email}
                  onChange={(event) => setEditForm({ ...editForm, email: event.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Role</label>
                <select
                  className="input-field"
                  value={editForm.role}
                  onChange={(event) =>
                    setEditForm({ ...editForm, role: event.target.value as PlatformRole })
                  }
                >
                  <option value="admin">Admin</option>
                  <option value="user">Customer</option>
                  <option value="driver">Driver</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">KYC Status</label>
                <select
                  className="input-field"
                  value={editForm.kyc_status}
                  onChange={(event) =>
                    setEditForm({ ...editForm, kyc_status: event.target.value as KycStatus })
                  }
                >
                  <option value="none">None</option>
                  <option value="pending">Pending</option>
                  <option value="verified">Verified</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setEditingUser(null)}
                className="px-4 py-2 text-sm font-medium text-on-surface-variant bg-surface-container-low hover:bg-surface-container-highest hover:text-on-surface rounded-md"
              >
                Cancel
              </button>
              <button onClick={saveEdit} disabled={updateMutation.isLoading} className="btn-primary">
                {updateMutation.isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-table-card">
        <div className="admin-table-toolbar px-4 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-on-surface">User Directory</h2>
            <p className="mt-1 text-sm text-on-surface-variant">
              Browse platform identity records with tenant context and password recovery actions.
            </p>
          </div>
          <div className="text-sm text-on-surface-variant">
            {isLoading ? 'Loading users...' : `${filteredUsers.length} of ${users.length} users shown`}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="admin-table-grid min-w-full">
            <thead className="admin-table-head">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-on-surface-variant uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Tenant</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Access</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Dates</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="admin-table-body">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center">
                    <UsersIcon className="mx-auto h-12 w-12 text-on-surface-variant" />
                    <h3 className="mt-2 text-sm font-medium text-on-surface">No users match these filters</h3>
                    <p className="mt-1 text-sm text-on-surface-variant">Adjust the search or filter state to broaden the directory.</p>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user: PlatformUser) => {
                  const companyUrl = getCompanyUrl(user.company_slug);

                  return (
                    <tr key={user.id} className="admin-table-row align-top transition-colors">
                      <td className="px-4 py-4 min-w-[260px]">
                        <div className="flex items-start gap-3">
                          <div className="admin-avatar-tile h-11 w-11 rounded-lg bg-info-container text-on-info-container flex items-center justify-center font-semibold">
                            {getInitials(user.email)}
                          </div>
                          <div>
                            <div className="font-medium text-on-surface">{user.email}</div>
                            <div className="mt-1 text-xs text-on-surface-variant">{user.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 min-w-[220px]">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <BuildingOfficeIcon className="h-4 w-4 text-on-surface-variant" />
                            <span className="text-sm text-on-surface">{user.company_name || 'No company'}</span>
                          </div>
                          {user.company_slug && (
                            <span className="inline-flex px-2 py-0.5 rounded text-xs bg-surface-container-low text-on-surface-variant">
                              {user.company_slug}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 min-w-[220px]">
                        <div className="space-y-2">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${roleBadgeClass(user.role)}`}>
                            {roleLabel(user.role)}
                          </span>
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${kycBadgeClass(user.kyc_status)}`}>
                            {user.kyc_status || 'none'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 min-w-[160px]">
                        <div className="text-sm text-on-surface">Created {formatDate(user.created_at)}</div>
                        <div className="mt-1 text-xs text-on-surface-variant">Updated {formatDate(user.updated_at)}</div>
                      </td>
                      <td className="px-4 py-4 min-w-[260px]">
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            onClick={() => startEdit(user)}
                            className="admin-action-pill px-3 py-1.5 rounded-md bg-info-container text-on-info-container hover:brightness-110 text-sm font-medium inline-flex items-center gap-1"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleResetPassword(user)}
                            className="admin-action-pill px-3 py-1.5 rounded-md bg-warning-container text-on-warning-container hover:brightness-110 text-sm font-medium inline-flex items-center gap-1 disabled:opacity-50"
                            disabled={resetPasswordMutation.isLoading}
                          >
                            <KeyIcon className="h-4 w-4" />
                            Reset Password
                          </button>
                          {companyUrl ? (
                            <a
                              href={companyUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="px-3 py-1.5 rounded-md bg-surface-container-low text-on-surface hover:bg-surface-container-highest text-sm font-medium inline-flex items-center gap-1"
                            >
                              <LinkIcon className="h-4 w-4" />
                              Open Tenant
                            </a>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </PageLayout>
  );
}

export default PlatformUsers;
